/**
 * Guardar la copia de seguridad en tu nube.
 *
 * ---------------------------------------------------------------------------
 *  Cómo se resuelve sin pedirte nada
 * ---------------------------------------------------------------------------
 *
 * Lo natural sería conectar con Google Drive o OneDrive: registrar una aplicación con
 * ellos, pedir permisos, guardar un token… Y eso solo lo puede hacer el dueño de la
 * cuenta. Llevaba rondas esperando esa decisión.
 *
 * Pero hay otra forma que **no necesita nada de nadie**: el menú de compartir del
 * propio teléfono. Se le entrega el archivo al sistema y él ofrece Drive, OneDrive,
 * Dropbox, WhatsApp o el correo. El usuario elige, y la copia acaba donde quiera.
 *
 * Es mejor por tres razones:
 *
 *  · **No hay que registrar nada** ni pedir permisos a nadie.
 *  · **No hay ningún token nuestro guardado** que se pueda robar.
 *  · **Funciona con cualquier servicio**, incluso con uno que no existía cuando se
 *    escribió esto.
 *
 * La contrapartida, que se dice en pantalla: hay que pulsar el botón. No hay copia
 * automática cada noche. Se puede tener, pero necesita la aplicación registrada con el
 * proveedor, y eso es una decisión aparte que no se toma a escondidas.
 */

/**
 * ¿Puede este dispositivo compartir archivos al sistema?
 *
 * No basta con que exista `navigator.share`: muchos navegadores lo tienen para texto y
 * enlaces pero **no aceptan archivos**. Y hay que preguntarlo con el archivo delante,
 * porque `canShare` decide según el tipo y el tamaño.
 */
export function canShareFiles(file: File): boolean {
  if (typeof navigator === 'undefined') return false;
  if (typeof navigator.share !== 'function') return false;
  if (typeof navigator.canShare !== 'function') return false;

  try {
    return navigator.canShare({ files: [file] });
  } catch {
    return false;
  }
}

/** El respaldo, como archivo listo para entregar al sistema. */
export async function fetchBackupFile(): Promise<File> {
  const response = await fetch('/api/reports/export.json', {
    // La sesión viaja en la cookie, no en una cabecera.
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('No se pudo preparar el respaldo.');
  }

  const blob = await response.blob();
  const today = new Date().toISOString().slice(0, 10);

  return new File([blob], `eldescuadre-respaldo-${today}.json`, {
    type: 'application/json',
  });
}

/**
 * Entrega el respaldo al menú de compartir del sistema.
 *
 * Devuelve `false` si el usuario cerró el menú sin elegir nada. **No es un error**: es
 * lo que pasa cuando alguien abre el menú para mirar y decide que no. Tratarlo como
 * fallo haría salir un mensaje rojo por algo que no se ha roto.
 */
export async function shareBackup(file: File): Promise<boolean> {
  try {
    await navigator.share({
      files: [file],
      title: 'Respaldo de ElDescuadre',
      text: 'Copia de seguridad de mis finanzas.',
    });
    return true;
  } catch (error) {
    // `AbortError` es «el usuario cerró el menú». Cualquier otra cosa sí es un fallo.
    if ((error as { name?: string } | null)?.name === 'AbortError') return false;
    throw error;
  }
}
